// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import {
  InfoOutlined,
  WarningAmberRounded,
} from '@/constants/hardfin-standard-icons';
import { Project } from '@/project/api/types';
import { ProjectKeyDateControl } from '@/project/components/project-key-date-control';
import { getProjectKeyDateWarnings } from '@/project/utilities/project-key-date-warnings';
import { Stack, Tooltip, Typography } from '@mui/material';
import { Fragment, ReactNode } from 'react';

interface KeyDateRowProps {
  /** The date description shown on the left side of the row */
  label: string;
  /** Explanation of the date, shown in an info tooltip next to the label */
  tooltip: string;
  /** Advisory notices shown as a warning icon next to the date */
  warnings: string[];
  /** The editable date control shown on the right side of the row */
  children: ReactNode;
}

function KeyDateRow(props: KeyDateRowProps) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle2">{props.label}</Typography>
        <Tooltip title={props.tooltip} placement="top" arrow>
          <InfoOutlined
            color="action"
            sx={{ marginLeft: 0.5, fontSize: '16px' }}
          />
        </Tooltip>
      </Stack>
      <Stack direction="row" alignItems="center">
        {props.children}
        {props.warnings.length > 0 && (
          <Tooltip
            title={props.warnings.map((warning, index) => (
              <Fragment key={warning}>
                {index > 0 && <br />}
                {warning}
              </Fragment>
            ))}
            placement="top"
            arrow
          >
            <WarningAmberRounded
              sx={{
                marginLeft: 0.5,
                fontSize: '16px',
                color: 'warning.main',
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}

interface Props {
  project: Project;
}

/**
 * Section component listing the project's key dates with inline editing.
 * Rendered in the right gutter of the project detail page; designed to grow
 * as more project dates are added over time.
 */
export function ProjectKeyDatesSection(props: Props) {
  return (
    <HardfinTabCardWrapper title="Key dates" hideDivider>
      <Stack spacing={1}>
        <KeyDateRow
          label="Contract date"
          tooltip="The date the contract was signed or agreed upon (optional)"
          warnings={getProjectKeyDateWarnings(
            props.project,
            'contractSignedOn'
          )}
        >
          <ProjectKeyDateControl
            project={props.project}
            field="contractSignedOn"
          />
        </KeyDateRow>
        <KeyDateRow
          label="Site visit date"
          tooltip="The date the site was visited or is planned for a visit (optional)"
          warnings={getProjectKeyDateWarnings(props.project, 'siteVisitDate')}
        >
          <ProjectKeyDateControl
            project={props.project}
            field="siteVisitDate"
          />
        </KeyDateRow>
        <KeyDateRow
          label="Install date"
          tooltip="The date the project was installed or is planned for installation (optional)"
          warnings={getProjectKeyDateWarnings(props.project, 'installDate')}
        >
          <ProjectKeyDateControl project={props.project} field="installDate" />
        </KeyDateRow>
        <KeyDateRow
          label="Training date"
          tooltip="The date training was delivered or is planned for delivery (optional)"
          warnings={getProjectKeyDateWarnings(props.project, 'trainingDate')}
        >
          <ProjectKeyDateControl project={props.project} field="trainingDate" />
        </KeyDateRow>
        <KeyDateRow
          label="Go-live date"
          tooltip="The date the project went live or is planned to go live (optional)"
          warnings={getProjectKeyDateWarnings(props.project, 'goLiveDate')}
        >
          <ProjectKeyDateControl project={props.project} field="goLiveDate" />
        </KeyDateRow>
        <KeyDateRow
          label="Acceptance date"
          tooltip="The date the project was accepted or is planned for acceptance (optional)"
          warnings={getProjectKeyDateWarnings(props.project, 'acceptanceDate')}
        >
          <ProjectKeyDateControl
            project={props.project}
            field="acceptanceDate"
          />
        </KeyDateRow>
      </Stack>
    </HardfinTabCardWrapper>
  );
}
